/**
 * Çubuk grafiklerin **sayı ekseni** ölçeği (077 §K5). Bileşenden ayrı bir modüldür: hem ekseni çizen
 * `BarChartAxis` hem çubukları çizen çağıran <b>aynı</b> üst sınırı kullanmak zorundadır (aksi hâlde en
 * uzun çubuk eksenin son işaretini taşar) — ortak fonksiyonu bileşen dosyasından dışa açmak, dosyayı
 * "yalnız bileşen dışa aktarır" kuralından çıkarırdı.
 */

/**
 * Varsayılan hedef işaret sayısı. **10**'dur, 5 değil: mockup'ın ekseni 0 · 20 · 40 … 200 diziyor ve
 * 184'lük bir max, 5 hedefle 50'şer adıma (0 · 50 · 100 · 150 · 200) düşerdi.
 */
const DEFAULT_TARGET_TICKS = 10

/**
 * 1 · 2 · 5 × 10ⁿ ailesinden, hedef işaret sayısına en yakın "okunur" adım.
 *
 * <p>Ham `max` doğrudan bölünseydi eksen 0 · 18,4 · 36,8 … gibi okunmaz değerler üretirdi; mockup'taki
 * 0 · 20 · 40 … 200 dizisi bu yuvarlamadan çıkar.
 *
 * <p><b>Adım en az 1'dir.</b> Bu eksen <b>adet</b> gösterir (aksiyon sayısı) ve "yarım aksiyon" diye bir
 * şey yoktur. Sınır olmasaydı 3'lük bir max'ta adım 0,5 çıkar, işaretler yuvarlanırken
 * {@code 0 · 1 · 1 · 2 · 2 · 3} gibi <b>yinelenen</b> değerler üretirdi (React'te yinelenen anahtar,
 * ekranda ise anlamsız bir eksen).
 */
function niceStep(max: number, targetTicks: number): number {
  const raw = max / targetTicks
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const normalized = raw / magnitude
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return Math.max(1, step * magnitude)
}

/**
 * Eksenin işaretleri ve **yuvarlanmış üst sınırı**. Çubukları çizen taraf da ölçek tabanı olarak
 * {@link AxisScale.upper} kullanmalıdır.
 *
 * <p>`max <= 0` (hiç veri yok) → tek işaretli dejenere ölçek: bölme yapılmaz, çubuklar boş ray kalır.
 */
export interface AxisScale {
  upper: number
  ticks: number[]
}

export function axisScale(max: number, targetTicks = DEFAULT_TARGET_TICKS): AxisScale {
  if (max <= 0) return { upper: 1, ticks: [0] }
  const step = niceStep(max, targetTicks)
  const upper = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let value = 0; value <= upper + step / 2; value += step) ticks.push(Math.round(value))
  return { upper, ticks }
}
